import React from 'react';
import { Edit3, Save, X, CheckCircle } from 'lucide-react';
import Portal from './Portal';

/**
 * ProfileEditFAB - Floating Action Button for Profile Edit/Save
 * 
 * Features:
 * - Single FAB that transforms between Edit and Save states
 * - Small cancel button appears above FAB when in edit mode
 * - Proper z-index positioning to avoid conflict with QuickActionsFAB
 * - Mobile safe area support
 * - Accessible with proper ARIA labels
 * 
 * @param {Object} props
 * @param {boolean} props.isEditing - Whether currently in edit mode
 * @param {boolean} props.hasChanges - Whether there are unsaved changes
 * @param {boolean} props.saving - Whether save operation is in progress
 * @param {Function} props.onEdit - Callback when Edit is clicked
 * @param {Function} props.onSave - Callback when Save is clicked
 * @param {Function} props.onCancel - Callback when Cancel is clicked
 */
const ProfileEditFAB = React.memo(({ 
  isEditing, 
  hasChanges, 
  saving, 
  onEdit, 
  onSave, 
  onCancel 
}) => {
  const handleFabClick = () => {
    if (!isEditing) {
      onEdit();
    } else if (hasChanges) {
      onSave();
    } else {
      // In edit mode with no changes: exit cleanly without hitting save logic
      onCancel();
    }
  };

  // Three distinct visual states: Edit (blue) | Done/no-changes (gray) | Save (green)
  const getFabStyles = () => {
    if (isEditing && hasChanges) {
      return 'bg-gradient-to-r from-green-600 to-green-700 focus-visible:ring-green-500/50';
    } else if (isEditing && !hasChanges) {
      return 'bg-gradient-to-r from-gray-500 to-gray-600 focus-visible:ring-gray-400/50';
    } else {
      return 'bg-gradient-to-r from-blue-700 to-blue-800 focus-visible:ring-blue-500/50';
    }
  };

  const getFabIcon = () => {
    if (saving) {
      return (
        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
      );
    } else if (isEditing && hasChanges) {
      return <Save className="w-6 h-6" />;
    } else if (isEditing && !hasChanges) {
      return <CheckCircle className="w-6 h-6" />;
    } else {
      return <Edit3 className="w-6 h-6" />;
    }
  };

  const getFabAriaLabel = () => {
    if (saving) {
      return 'Saving profile changes...';
    } else if (isEditing && hasChanges) {
      return 'Save profile changes';
    } else if (isEditing && !hasChanges) {
      return 'Done — exit edit mode';
    } else {
      return 'Edit profile';
    }
  };

  return (
    <Portal>
      <div className="fixed bottom-20 right-6 z-[9997] pb-safe pr-safe md:hidden">
        {/* Cancel Button - Only visible in edit mode */}
        {isEditing && (
          <button
            onClick={onCancel}
            disabled={saving}
            className="absolute -top-12 right-0 w-10 h-10 bg-white border-2 border-gray-300 text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gray-300/50 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Cancel editing and discard changes"
            title="Cancel"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* FAB Button */}
        <button
          onClick={handleFabClick}
          disabled={saving}
          className={`w-14 h-14 ${getFabStyles()} text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
            saving ? 'cursor-wait' : ''
          }`}
          aria-label={getFabAriaLabel()}
          aria-busy={saving}
        >
          {getFabIcon()}
        </button>
      </div>
    </Portal>
  );
});

ProfileEditFAB.displayName = 'ProfileEditFAB';

export default ProfileEditFAB;
